// ============================================
// Settings Store - Zustand
// Handles holding non-saved local settings state to prevent reset when switching pages/unmounting
// ============================================

import { create } from 'zustand';

export interface GeneralSettings {
  storeName: string;
  storeEmail: string;
  timezone: string;
  currency: string;
  language: string;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  lowStockAlerts: boolean;
  orderNotifications: boolean;
  dailyReports: boolean;
  weeklyDigest: boolean;
}

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  ipWhitelist: string;
}

interface SettingsStoreState {
  // Local changes state
  localGeneral: GeneralSettings | null;
  localNotifications: NotificationSettings | null;
  localSecurity: SecuritySettings | null;
  isDirty: boolean;

  // Actions
  setLocalGeneral: (settings: GeneralSettings | ((prev: GeneralSettings) => GeneralSettings)) => void;
  setLocalNotifications: (settings: NotificationSettings | ((prev: NotificationSettings) => NotificationSettings)) => void;
  setLocalSecurity: (settings: SecuritySettings | ((prev: SecuritySettings) => SecuritySettings)) => void;
  
  // Clear/Reset
  clearLocalState: () => void;
}

export const useSettingsStore = create<SettingsStoreState>((set) => ({
  localGeneral: null,
  localNotifications: null,
  localSecurity: null,
  isDirty: false,

  setLocalGeneral: (settings) =>
    set((state) => {
      const nextVal = typeof settings === 'function' 
        ? settings(state.localGeneral || { storeName: '', storeEmail: '', timezone: 'Asia/Ho_Chi_Minh', currency: 'VND', language: 'vi' }) 
        : settings;
      return { localGeneral: nextVal, isDirty: true };
    }),

  setLocalNotifications: (settings) =>
    set((state) => {
      const nextVal = typeof settings === 'function'
        ? settings(state.localNotifications || { emailNotifications: false, lowStockAlerts: false, orderNotifications: false, dailyReports: false, weeklyDigest: false })
        : settings;
      return { localNotifications: nextVal, isDirty: true };
    }),

  setLocalSecurity: (settings) =>
    set((state) => {
      const nextVal = typeof settings === 'function'
        ? settings(state.localSecurity || { twoFactorEnabled: false, sessionTimeout: 30, passwordExpiry: 0, ipWhitelist: '' })
        : settings;
      return { localSecurity: nextVal, isDirty: true };
    }),

  clearLocalState: () =>
    set({
      localGeneral: null,
      localNotifications: null,
      localSecurity: null,
      isDirty: false,
    }),
}));
